import type { ReactNode } from 'react';

/* StyledProps — the kit-wide style contract.
   Audit #11 (2026-05-16): `style` removed entirely. Free-form inline
   CSS belongs to `UnsafeProps.unsafe_style` (see escape-hatches.ts) so
   that every adopter call site that bypasses the design system says
   `unsafe_` out loud. Primitives that previously accepted `style` via
   StyledProps now extend `UnsafeProps` explicitly when inline-CSS
   forwarding is the intent. */
export interface StyledProps {
  className?: string;
}

export interface WithChildren {
  children?: ReactNode;
}

/* TintName — kit's semantic tint vocabulary.
   Audit #9 (2026-05-16): renamed from v4 poetic aliases
   (glycine/vert/rose/jaune/bleu/encre/blanc) to v5 semantic names that
   match the underlying `--color-{tint}-*` token series in tokens.css.
   The previous values referenced CSS vars (e.g., `--vert`) that were
   never defined in v5's tokens, so `tone="vert"` on DisplayNum/LedgerRow
   silently resolved to nothing. The semantic names below each map
   1:1 to a working `--color-{tint}-{stop}` series. */
export type TintName = 'theme' | 'success' | 'error' | 'warning' | 'info';
export type ProgressTint = TintName;

/* Single source of truth for runtime tint validation. Primitives
   that interpolate a tint name into a CSS class or var() fallback
   check membership against these sets rather than duplicating the
   literal tuple at each call site. */
export const TINT_NAMES = new Set<TintName>(['theme', 'success', 'error', 'warning', 'info']);

export const PROGRESS_TINTS = new Set<ProgressTint>([
  'theme',
  'success',
  'error',
  'warning',
  'info',
]);

/* Resolve a TintName to its v5 token CSS variable for inline color
   declarations. Used by primitives that emit `color: var(--…)` rather
   than a class. Returns the 700-stop by default (text contrast); pass
   `stop` to target other stops (100 = subtle bg, 500 = mid, 700 = text).
   Returns `undefined` for unknown tint values so a typo can't inject
   arbitrary CSS through any fallback path. */
export function tintVar(
  tint: TintName | undefined,
  stop: 100 | 500 | 700 = 700,
): string | undefined {
  if (!tint || !TINT_NAMES.has(tint)) return undefined;
  return `var(--color-${tint}-${stop})`;
}

export interface SelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
  /** @internal Render a divider line above this option. */
  _dividerBefore?: boolean;
  /** @internal Style as an action item (secondary color, no check gutter). */
  _action?: boolean;
}
